import { ofType } from 'redux-observable'
import {
  catchError,
  defer,
  EMPTY,
  filter,
  ignoreElements,
  interval,
  map,
  merge,
  mergeMap,
  of,
  startWith,
  Subject,
  switchMap,
  takeUntil,
  tap,
  timer
} from 'rxjs'
import type { WebSocketSubject, WebSocketSubjectConfig } from 'rxjs/webSocket'
import { webSocket } from 'rxjs/webSocket'
import {
  connectionReady,
  connectToServer,
  disconnectFromServer,
  receivedWebSocketMessage,
  reconnectToServer,
  reportBackendUnreachable,
  sendWebSocketMessage,
  sendWebSocketMessageDone,
  websocketUnauthorized
} from '../../Slice/Application/ApplicationSlice.js'
import type { Epic } from '../../index.js'
import { scan, withLatestFrom } from 'rxjs/operators'

/* Hand off to the connectivity layer after this many consecutive failed reconnects. */
const MAX_RECONNECT_ATTEMPTS = 10
/* Hard cap for the exponential backoff. */
const MAX_BACKOFF_MS = 30000
/* Fallback base delay when the connect payload doesn't carry a reconnectionTimeout. */
const DEFAULT_BASE_DELAY_MS = 1000

type WebSocketFactory = (config: WebSocketSubjectConfig<unknown>) => WebSocketSubject<unknown>

/*
 * Epic handling the lifecycle of a websocket connection.
 *
 * Scope: short-range recovery only. It reconnects a dropped socket on a capped
 * backoff, and once the attempts are exhausted it REPORTS rather than retries —
 * the connectivity layer decides when the backend is worth talking to again and
 * re-arms this epic with a fresh connectToServer (see reconnectWebsocketEpic).
 *
 * The socket factory is injectable purely so tests can drive open/close/error
 * events deterministically; production uses the real rxjs `webSocket`.
 */
export const makeConnectToServerEpic =
  (webSocketFactory: WebSocketFactory = webSocket): Epic =>
  (action$, state$) =>
    action$.pipe(
      /* Only a fresh connectToServer (re)builds the whole connection machinery.
       * Reconnects are driven by the inner reconnectToServer stream below so the
       * backoff counter survives across attempts instead of resetting every cycle. */
      filter(connectToServer.match),
      switchMap((action) => {
        // defer so each connectToServer freshly builds its own subjects/streams
        return defer(() => {
          const baseDelayMs = action.payload.reconnectionTimeout || DEFAULT_BASE_DELAY_MS

          /* A normal socket close is not an rxjs error, and the real onopen isn't
           * part of the message stream — so we bridge both into these subjects
           * from the socket's close/open observers. */
          const reconnectTrigger$ = new Subject<void>()
          const socketOpen$ = new Subject<void>()
          const socketUnauthorized$ = new Subject<void>()

          /* Backoff + hand-off. Subscribed ONCE per connectToServer (outside the
           * inner socket switchMap) so its `scan` state survives across reconnect
           * attempts. A genuine socket open resets the counter; each failure
           * increments it and schedules the next reconnect with capped exponential
           * backoff. Past the cap we stop and let the connectivity layer take over. */
          const reconnectRetry$ = merge(
            reconnectTrigger$.pipe(map(() => 'retry' as const)),
            socketOpen$.pipe(map(() => 'open' as const))
          ).pipe(
            scan(
              (state, signal) => (signal === 'open' ? { signal, count: 0 } : { signal, count: state.count + 1 }),
              { signal: 'open' as 'open' | 'retry', count: 0 }
            ),
            mergeMap(({ signal, count }) => {
              if (signal === 'open') return EMPTY
              if (count > MAX_RECONNECT_ATTEMPTS) {
                /* Stop retrying here and hand off exactly once: the connectivity
                 * layer owns the offline verdict, probes /health on its own capped
                 * backoff, and re-arms us through reconnectWebsocketEpic when the
                 * backend answers again. Continuing to retry in parallel would just
                 * duplicate that — and retrying forever from here is what used to
                 * hammer every open tab. */
                return count === MAX_RECONNECT_ATTEMPTS + 1 ? of(reportBackendUnreachable()) : EMPTY
              }
              const backoffMs = Math.min(baseDelayMs * Math.pow(2, count - 1), MAX_BACKOFF_MS)
              return timer(backoffMs).pipe(map(() => reconnectToServer(action.payload)))
            }),
            takeUntil(action$.pipe(ofType(connectToServer.type, disconnectFromServer.type)))
          )

          /* connectionReady is raised on a GENUINE socket open only (never on a
           * mere connection attempt) — see ResyncOnReconnectEpic, its sole consumer.
           *
           * isReconnect is read from state BEFORE this action updates it, and is
           * global rather than per-socket on purpose: the machinery is rebuilt from
           * scratch on every connectToServer (including the re-arm after an
           * outage), so anything scoped to one build would call a reconnect a first
           * connect and skip the resubscribe. */
          const connectionReady$ = socketOpen$.pipe(
            withLatestFrom(state$),
            map(([, state]) => connectionReady({ isReconnect: state.application.hasConnectedBefore })),
            takeUntil(action$.pipe(ofType(disconnectFromServer.type)))
          )

          /* Close code 3000 is an auth rejection, not an outage: no reconnect here
           * (the token would still be bad), and reconnectWebsocketEpic revives the
           * socket once the token has been refreshed. */
          const unauthorized$ = socketUnauthorized$.pipe(
            map(() => websocketUnauthorized()),
            takeUntil(action$.pipe(ofType(disconnectFromServer.type)))
          )

          const socket$ = action$.pipe(
            ofType(reconnectToServer.type),
            takeUntil(action$.pipe(ofType(disconnectFromServer.type))),
            startWith(null),
            map(() =>
              /* actual socket connection */
              webSocketFactory({
                protocol: action.payload.protocolVersion,
                url: action.payload.url,
                WebSocketCtor: WebSocket,
                /* Fires on the real onopen — the ONLY place connectionReady is raised. */
                openObserver: {
                  next: () => socketOpen$.next()
                },
                /* close always fires (including after an error), so it is the single
                 * source of reconnect triggers. The incoming-stream catchError below
                 * only logs — that avoids a double reconnect for one failure. */
                closeObserver: {
                  next: (event) => {
                    console.info('WebSocket closed:', {
                      code: event.code,
                      reason: event.reason,
                      wasClean: event.wasClean
                    })
                    if (![1000, 1001, 3000].includes(event.code)) {
                      console.info('WebSocket closed abnormally:', {
                        code: event.code,
                        reason: event.reason,
                        wasClean: event.wasClean
                      })
                    }
                    if (event.code !== 3000) {
                      reconnectTrigger$.next()
                    } else {
                      console.info('Not authorized, terminating socket')
                      socketUnauthorized$.next()
                    }
                  }
                }
              })
            ),
            switchMap((webSocketConnection$: WebSocketSubject<unknown>) =>
              merge(
                // Heartbeats
                interval(30000).pipe(
                  tap(() => webSocketConnection$.next({ type: 'ping' })),
                  ignoreElements()
                ),

                // Incoming messages
                webSocketConnection$.pipe(
                  takeUntil(action$.pipe(ofType(reconnectToServer.type, disconnectFromServer.type))),
                  catchError((error) => {
                    /* Do NOT trigger a reconnect here: the closeObserver above always
                     * fires on close (including right after an error) and owns the
                     * single reconnect trigger. */
                    console.info('WebSocket errored:', error)
                    return EMPTY
                  }),
                  map(receivedWebSocketMessage)
                ),

                // Outgoing messages
                action$.pipe(
                  filter(sendWebSocketMessage.match),
                  mergeMap((sendAction) => {
                    webSocketConnection$.next(sendAction.payload)
                    return of(sendWebSocketMessageDone(sendAction.payload))
                  })
                )
              )
            )
          )

          return merge(socket$, connectionReady$, unauthorized$, reconnectRetry$)
        })
      })
    )

/* Production epic wired into the store (real rxjs webSocket factory). */
export const connectToServerEpic = makeConnectToServerEpic()
